import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const API_URL = API_BASE_URL;

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.light,
      }}>
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            fontSize: '1.5rem',
            color: colors.primary[600],
            fontWeight: 600,
          }}
        >
          Loading profile...
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.light,
        padding: spacing.lg,
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            textAlign: 'center',
            padding: spacing.xl,
            backgroundColor: 'white',
            borderRadius: spacing.md,
            boxShadow: shadows.card,
          }}
        >
          <p style={{
            fontSize: '1.125rem',
            color: colors.text.primary,
            fontWeight: 600,
            marginBottom: spacing.md,
          }}>
            Please login to view your profile
          </p>
          <a
            href="/user-login"
            style={{
              display: 'inline-block',
              padding: `${spacing.sm} ${spacing.lg}`,
              backgroundColor: colors.primary[600],
              color: 'white',
              textDecoration: 'none',
              borderRadius: spacing.sm,
              fontWeight: 600,
              transition: 'background-color 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.primary[700];
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.primary[600];
            }}
          >
            Go to Login
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.background.light,
      padding: `${spacing.xl} ${spacing.lg}`,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        {/* Profile Header */}
        <div style={{
          marginBottom: spacing.xl,
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            color: colors.text.primary,
            margin: 0,
            marginBottom: spacing.sm,
          }}>
            Profile
          </h1>
          <div style={{
            height: '4px',
            width: '80px',
            backgroundColor: colors.primary[600],
            borderRadius: '2px',
          }} />
        </div>

        {user && (
          <>
            {/* User Info Card */}
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              style={{
                backgroundColor: 'white',
                borderRadius: spacing.lg,
                boxShadow: shadows.card,
                padding: spacing.xl,
                marginBottom: spacing.lg,
              }}
            >
              <div style={{ marginBottom: spacing.lg }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.text.secondary,
                  marginBottom: spacing.xs,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Full Name
                </label>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: colors.text.primary,
                  padding: `${spacing.md} ${spacing.lg}`,
                  backgroundColor: colors.primary[50],
                  borderRadius: spacing.sm,
                  border: `2px solid ${colors.primary[200]}`,
                }}>
                  {user.name}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.text.secondary,
                  marginBottom: spacing.xs,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Phone Number
                </label>
                <div style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: colors.text.primary,
                  padding: `${spacing.md} ${spacing.lg}`,
                  backgroundColor: colors.primary[50],
                  borderRadius: spacing.sm,
                  border: `2px solid ${colors.primary[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                }}>
                  <span>📱</span>
                  {user.phone && user.phone.length === 10 ? `+91 ${user.phone}` : user.phone || 'Not provided'}
                </div>
              </div>
            </motion.div>

            {/* Account Actions */}
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              style={{
                backgroundColor: 'white',
                borderRadius: spacing.lg,
                boxShadow: shadows.card,
                padding: spacing.xl,
              }}
            >
              <h2 style={{
                fontSize: '1.125rem',
                fontWeight: 700,
                color: colors.text.primary,
                marginBottom: spacing.lg,
                margin: 0,
              }}>
                Account Actions
              </h2>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                style={{
                  width: '100%',
                  padding: `${spacing.md} ${spacing.lg}`,
                  marginTop: spacing.lg,
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: spacing.sm,
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing.sm,
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ef4444';
                }}
              >
                Logout
              </motion.button>
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default Profile;
